import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Search,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Loader2,
  ArrowRight,
  ExternalLink,
  Clock
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value, currency = "GBP") => {
  if (!value || value === 0) return "N/A";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: currency === "USD" ? "USD" : "GBP",
    minimumFractionDigits: 2,
  }).format(value);
};

const formatMarketCap = (value) => {
  if (!value) return "N/A";
  if (value >= 1e12) return `£${(value / 1e12).toFixed(2)}T`;
  if (value >= 1e9) return `£${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `£${(value / 1e6).toFixed(2)}M`;
  return value.toString();
};

const getRecommendationClass = (rec) => {
  const classes = {
    STRONG_BUY: "rec-strong-buy",
    BUY: "rec-buy",
    HOLD: "rec-hold",
    SELL: "rec-sell",
    STRONG_SELL: "rec-strong-sell",
  };
  return classes[rec] || "rec-hold";
};

const getConfidenceClass = (score) => {
  if (score >= 70) return "confidence-high";
  if (score >= 50) return "confidence-medium";
  return "confidence-low";
};

export const DeepResearch = () => {
  const { symbol: urlSymbol } = useParams();
  const navigate = useNavigate();
  const [stocks, setStocks] = useState([]);
  const [selectedSymbol, setSelectedSymbol] = useState(urlSymbol || "");
  const [searchQuery, setSearchQuery] = useState("");
  const [research, setResearch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingStocks, setLoadingStocks] = useState(true);

  useEffect(() => {
    fetchAvailableStocks();
  }, []);

  useEffect(() => {
    if (urlSymbol) {
      setSelectedSymbol(urlSymbol);
      fetchResearch(urlSymbol);
    }
  }, [urlSymbol]);

  const fetchAvailableStocks = async () => {
    try {
      const response = await axios.get(`${API}/stocks/available`);
      setStocks(response.data.stocks || []);
    } catch (error) {
      console.error("Error fetching stocks:", error);
    } finally {
      setLoadingStocks(false);
    }
  };

  const fetchResearch = async (symbol) => {
    if (!symbol) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/research/${symbol}`);
      setResearch(response.data);
    } catch (error) {
      console.error("Error fetching research:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStockSelect = (symbol) => {
    setSelectedSymbol(symbol);
    navigate(`/research/${symbol}`);
    fetchResearch(symbol);
  };

  const filteredStocks = stocks.filter(
    (stock) =>
      stock.symbol.toLowerCase().includes(searchQuery.toLowerCase()) ||
      stock.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-slide-up" data-testid="deep-research-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          Deep Research
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          AI-powered analysis for UK & US stocks available on Hargreaves Lansdown
        </p>
      </div>

      {/* Stock Selector */}
      <Card data-testid="stock-selector-card">
        <CardHeader className="pb-4">
          <CardTitle className="text-sm uppercase tracking-widest font-medium" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Select Stock to Analyze
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search stocks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="stock-search-input"
              />
            </div>
            <Select value={selectedSymbol} onValueChange={handleStockSelect}>
              <SelectTrigger className="w-full sm:w-[280px]" data-testid="stock-select-trigger">
                <SelectValue placeholder="Select a stock" />
              </SelectTrigger>
              <SelectContent>
                {loadingStocks ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    Loading stocks...
                  </div>
                ) : (
                  filteredStocks.map((stock) => (
                    <SelectItem key={stock.symbol} value={stock.symbol}>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{stock.symbol}</span>
                        <span className="text-muted-foreground text-xs">
                          {stock.name}
                        </span>
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Quick Select Buttons */}
          <div className="mt-4">
            <p className="text-xs text-muted-foreground mb-2">Quick Select (Top Momentum):</p>
            <div className="flex flex-wrap gap-2">
              {stocks.slice(0, 6).map((stock) => (
                <Button
                  key={stock.symbol}
                  variant={selectedSymbol === stock.symbol ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleStockSelect(stock.symbol)}
                  className="text-xs"
                  data-testid={`quick-select-${stock.symbol}`}
                >
                  {stock.symbol}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Loading State */}
      {loading && (
        <Card data-testid="research-loading">
          <CardContent className="py-12">
            <div className="flex flex-col items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
              <p className="text-sm text-muted-foreground">
                AI is analyzing {selectedSymbol}...
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                This may take a few seconds
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Research Results */}
      {research && !loading && (
        <div className="space-y-6 fade-in">
          {/* Hero Section */}
          <Card className="gradient-glow" data-testid="research-hero-card">
            <CardContent className="pt-6">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-3xl font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                      {research.name}
                    </h2>
                    <Badge variant="outline" className="text-xs">
                      {research.symbol}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                    <Clock className="w-4 h-4" />
                    <span>
                      Analysis Date: {new Date(research.analysis_date).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-base leading-relaxed">
                    {research.executive_summary}
                  </p>
                </div>
                <div className="flex flex-col items-center p-6 bg-muted/30 rounded-lg min-w-[200px]">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
                    AI Recommendation
                  </p>
                  <Badge className={`text-lg px-4 py-2 ${getRecommendationClass(research.ai_recommendation)}`}>
                    {research.ai_recommendation}
                  </Badge>
                  <div className="mt-4 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Confidence Score</p>
                    <div className={`text-3xl font-bold ${getConfidenceClass(research.ai_confidence)}`}>
                      {research.ai_confidence?.toFixed(0)}%
                    </div>
                  </div>
                  {research.target_price && (
                    <div className="mt-4 text-center">
                      <p className="text-xs text-muted-foreground mb-1">Target Price</p>
                      <p className="text-xl font-bold text-primary">
                        {formatCurrency(research.target_price, research.financial_highlights?.currency)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Financial Highlights */}
          <Card data-testid="financial-highlights-card">
            <CardHeader>
              <CardTitle className="text-sm uppercase tracking-widest font-medium" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Financial Highlights
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-muted/30 rounded-md">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                    Current Price
                  </p>
                  <p className="text-xl font-bold tabular-nums">
                    {formatCurrency(research.financial_highlights?.current_price, research.financial_highlights?.currency)}
                  </p>
                </div>
                <div className="p-4 bg-muted/30 rounded-md">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                    Market Cap
                  </p>
                  <p className="text-xl font-bold tabular-nums">
                    {formatMarketCap(research.financial_highlights?.market_cap)}
                  </p>
                </div>
                <div className="p-4 bg-muted/30 rounded-md">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                    P/E Ratio
                  </p>
                  <p className="text-xl font-bold tabular-nums">
                    {research.financial_highlights?.pe_ratio?.toFixed(2) || "N/A"}
                  </p>
                </div>
                <div className="p-4 bg-muted/30 rounded-md">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                    52W Range
                  </p>
                  <p className="text-sm font-medium tabular-nums">
                    {formatCurrency(research.financial_highlights?.["52w_low"], research.financial_highlights?.currency)} - {formatCurrency(research.financial_highlights?.["52w_high"], research.financial_highlights?.currency)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Investment Thesis */}
          <Card data-testid="investment-thesis-card">
            <CardHeader>
              <CardTitle className="text-sm uppercase tracking-widest font-medium" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Investment Thesis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-base leading-relaxed">
                {research.investment_thesis}
              </p>
            </CardContent>
          </Card>

          {/* Risks & Opportunities */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Key Risks */}
            <Card data-testid="key-risks-card">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-destructive" />
                  <CardTitle className="text-sm uppercase tracking-widest font-medium" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    Key Risks
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {research.key_risks?.map((risk, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <TrendingDown className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{risk}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Key Opportunities */}
            <Card data-testid="key-opportunities-card">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-success" />
                  <CardTitle className="text-sm uppercase tracking-widest font-medium" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    Key Opportunities
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {research.key_opportunities?.map((opp, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <TrendingUp className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{opp}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Sources */}
          <Card data-testid="sources-card">
            <CardHeader>
              <CardTitle className="text-sm uppercase tracking-widest font-medium" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Data Sources
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {research.sources?.map((source, index) => (
                  <Badge key={index} variant="outline" className="text-xs">
                    {source}
                  </Badge>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                Analysis powered by GPT-5.2. For informational purposes only.
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* No Selection State */}
      {!selectedSymbol && !loading && (
        <Card className="border-dashed" data-testid="no-selection-card">
          <CardContent className="py-12">
            <div className="text-center">
              <Search className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
              <h3 className="text-lg font-medium mb-2">Select a Stock to Analyze</h3>
              <p className="text-sm text-muted-foreground">
                Choose from stocks available on Hargreaves Lansdown for UK investors
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DeepResearch;
